import {
  DownOutlined,
  LoadingOutlined,
  QuestionOutlined
} from "@ant-design/icons"
import { App, Dropdown, Tag } from "antd"
import type { MenuItemType } from "antd/es/menu/interface"
import { type BaseOptionType } from "antd/lib/select"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import type { PendingCommitment } from "../../Projects/api/services.types.ts"
import { PendingCommitmentsAlert } from "../../Projects/components/PendingCommitmentsAlert.tsx"
import {
  isArchivingStatus,
  PROJECT_STATUSES_MAP,
  PROJECT_STATUSES_OBJ
} from "../../Projects/constants/project-statuses.ts"

interface ProjectStatusSelectorProps {
  status: null | string | number
  statuses?: BaseOptionType[]
  /** Required rather than optional, so the compiler names every picker. */
  canClose: boolean
  onChange: (
    id: number,
    options?: StatusChangeOptions
  ) => Promise<StatusChangeOutcome> | StatusChangeOutcome
}

export interface StatusChangeOptions {
  cancelAppointments?: boolean
}

export type StatusChangeOutcome =
  | { pendingCommitments?: PendingCommitment[] }
  | void

export function ProjectStatusSelector({
  status,
  statuses,
  canClose,
  onChange
}: ProjectStatusSelectorProps) {
  const { modal } = App.useApp()
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)

  if (!status) return <Tag className="m-0">No status</Tag>

  const getStatusData = () => {
    if (typeof status === "number") return PROJECT_STATUSES_OBJ?.[status]
    return PROJECT_STATUSES_MAP?.[status]
  }

  const statusData = getStatusData()

  const submit = async (value: number, cancelAppointments = false) => {
    setLoading(true)
    try {
      const outcome = await onChange(
        value,
        cancelAppointments ? { cancelAppointments: true } : undefined
      )
      const pending = outcome?.pendingCommitments
      if (pending?.length) confirmCommitments(value, pending)
    } finally {
      setLoading(false)
    }
  }

  const confirmCommitments = (
    value: number,
    commitments: PendingCommitment[]
  ) => {
    modal.confirm({
      centered: true,
      width: 520,
      icon: <QuestionOutlined />,
      title: t("projectArchive.commitments.title"),
      content: <PendingCommitmentsAlert commitments={commitments} />,
      cancelText: t("projectArchive.commitments.keepActive"),
      okText: t("projectArchive.commitments.confirm"),
      okButtonProps: { danger: true },
      onOk: () => submit(value, true)
    })
  }

  const handleChange = (value: number) => {
    modal.confirm({
      centered: true,
      icon: <QuestionOutlined />,
      title: "Confirm Status Change",
      content: "Are you sure you want to change the status of this project?",
      cancelText: "Cancel",
      okText: "Change",
      onOk: () => submit(value)
    })
  }

  const getDisabled = (s: BaseOptionType) => {
    if (typeof status === "number") return s?.value === status
    return s?.label?.toLowerCase() === status?.toLowerCase()
  }

  const items = statuses?.map((s) => {
    // Completing or cancelling archives the project, which takes its own right.
    const blockedByClose = !canClose && isArchivingStatus(s?.value)
    const isDisabled = getDisabled(s) || blockedByClose

    return {
      ...s,
      key: s?.value,
      className: `mb-1 ${isDisabled ? "" : "hover:outline-1 hover:outline-primary/50 transition-colors duration-500"}`,
      disabled: isDisabled || loading,
      // A plain `title`: a disabled item has no pointer-events wrapper for a Tooltip.
      title: blockedByClose ? t("projectArchive.closeBlocked") : undefined,
      onClick: () => handleChange(s?.value),
      // drop the status colour so antd's disabled style shows
      style: blockedByClose
        ? { opacity: "60%" }
        : {
            ...(PROJECT_STATUSES_OBJ?.[s?.value]?.style || {}),
            opacity: isDisabled ? "50%" : "100%"
          }
    }
  }) as MenuItemType[]

  return (
    <Dropdown
      menu={{
        items,
        selectedKeys: [status?.toString()]
      }}
      trigger={["click"]}
      disabled={loading}
    >
      <Tag style={statusData?.style} className="m-0 cursor-pointer">
        {statusData?.label || "No status"}
        {loading ? (
          <LoadingOutlined className="ml-1 text-xs" />
        ) : (
          <DownOutlined className="ml-1 text-xs" />
        )}
      </Tag>
    </Dropdown>
  )
}
